import { useState } from 'react';
import { useFeedback } from '../common/Feedback';
import { Modal } from '../common/Modal';
import { X, Eye, EyeOff } from 'lucide-react';

interface BulkUpdateModalProps {
  count: number;
  onSubmit: (payload: { login_plaintext?: string; password_plaintext?: string; totp_secret_plaintext?: string }) => Promise<void>;
  onClose: () => void;
}

export function BulkUpdateModal({ count, onSubmit, onClose }: BulkUpdateModalProps) {
  const { confirmar } = useFeedback();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showTotp, setShowTotp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasAnyField = !!(login.trim() || password.trim() || totpSecret.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasAnyField) {
      setError('Informe ao menos um campo para atualizar.');
      return;
    }
    if (!(await confirmar({ titulo: `Sobrescrever ${count} credencial(is)?`, mensagem: 'Os campos preenchidos substituem os valores atuais das credenciais selecionadas. Não dá para desfazer.', confirmar: 'Sobrescrever', perigo: true }))) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const payload: { login_plaintext?: string; password_plaintext?: string; totp_secret_plaintext?: string } = {};
    if (login.trim()) payload.login_plaintext = login.trim();
    if (password.trim()) payload.password_plaintext = password.trim();
    if (totpSecret.trim()) payload.totp_secret_plaintext = totpSecret.trim();

    try {
      await onSubmit(payload);
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar credenciais em massa');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal pedirParaFechar={onClose} protegerDigitacao>
      <div style={{
        background: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-xl)',
        width: '100%',
        maxWidth: '480px',
        padding: 'var(--space-8)',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
            Atualizar {count} credencia{count === 1 ? 'l' : 'is'} selecionada{count === 1 ? '' : 's'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar" title="Fechar" className="botao-icone botao-icone--sem-borda">
            <X size={24} />
          </button>
        </div>

        <div style={{
          padding: 'var(--space-3)',
          background: 'rgba(245, 158, 11, 0.08)',
          border: '1px solid rgba(245, 158, 11, 0.25)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-4)',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.5,
        }}>
          Apenas os campos preenchidos abaixo serão alterados, sobrescrevendo o valor atual em todas as credenciais selecionadas. Deixe em branco o que não quiser alterar. Rótulo, URL e demais dados não são afetados.
        </div>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Novo Login / CPF / OAB (opcional)</label>
            <input
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              placeholder="Deixe em branco para não alterar"
              style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Nova Senha (opcional)</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Deixe em branco para não alterar"
                style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', paddingRight: '40px', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', width: '100%', boxSizing: 'border-box' }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Novo Segredo TOTP (opcional)</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type={showTotp ? 'text' : 'password'}
                value={totpSecret}
                onChange={(e) => setTotpSecret(e.target.value)}
                placeholder="Deixe em branco para não alterar"
                style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', paddingRight: '40px', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', width: '100%', boxSizing: 'border-box' }}
              />
              <button type="button" onClick={() => setShowTotp(!showTotp)} style={{ position: 'absolute', right: '12px', background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                {showTotp ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-6)' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !hasAnyField}
              style={{ flex: 1, padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: (isSubmitting || !hasAnyField) ? 'not-allowed' : 'pointer', fontWeight: 'var(--font-medium)', opacity: (isSubmitting || !hasAnyField) ? 0.6 : 1 }}
            >
              {isSubmitting ? 'Atualizando...' : 'Atualizar credenciais'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
